/**
 * Changelog de Gestic — source unique de vérité.
 *
 * Règles de tenue (IMPORTANT) :
 *  - On n'ajoute une entrée QUE lorsqu'une fonctionnalité est réellement
 *    fonctionnelle en prod. Les allers-retours de debug (truc déployé puis
 *    re-corrigé) ne créent PAS d'entrée : on décrit le résultat final qui
 *    marche, une seule fois.
 *  - Ordre chronologique DESCENDANT : la version la plus récente en haut
 *    du tableau `CHANGELOG`.
 *  - `APP_VERSION` doit toujours pointer sur la 1re entrée (la plus récente).
 *
 * Versionnage par LOTS (workflow) :
 *  - La 1re entrée est la version EN COURS (`inProgress: true`, sans `date`).
 *    On y ACCUMULE les changements au fil de l'eau (feat / fix / improve).
 *  - On ne bump PAS à chaque update. Quand le lot est assez consistant pour
 *    être « livré » : on retire `inProgress`, on pose la `date` du jour, et
 *    le PROCHAIN changement crée une nouvelle entrée en cours en tête avec
 *    le numéro suivant (+0.1) — qui devient le nouvel `APP_VERSION`.
 *
 * Affiché dans la modale « Notes de version » (bouton à côté de la version,
 * en bas de la sidebar).
 */

export type ChangeType = "feat" | "fix" | "improve";

export interface ChangelogEntry {
  version: string;
  /** Date ISO (YYYY-MM-DD) de mise en prod. Absente tant que la version est en cours. */
  date?: string;
  /** Titre court de la version. */
  title?: string;
  /** Version EN COURS : lot pas encore scellé (on y ajoute encore des changements). */
  inProgress?: boolean;
  changes: { type: ChangeType; text: string }[];
}

/** Version courante affichée dans l'app. Toujours = CHANGELOG[0].version. */
export const APP_VERSION = "1.6";

export const CHANGELOG: ChangelogEntry[] = [
  {
    version: "1.6",
    inProgress: true,
    title: "Trésorerie limpide, vie commune & Light",
    changes: [
      {
        type: "feat",
        text: "Trésorerie — refonte complète du graphique : panneau de détail fixe sous la courbe (survole ou CLIQUE un mois pour l'épingler), et chaque chiffre est cliquable → une modale décompose le montant ligne à ligne. Tous les chiffres collent désormais au tableau principal au centime près (réalisé = solde à jour, prévisionnel chaîné comme le tableau).",
      },
      {
        type: "feat",
        text: "Trésorerie — la couche « Budgets » (toggle en en-tête) est scindée en deux : orange hachuré = budgets à consommer, BLEU = épargne à verser (cet argent reste à toi, il part au patrimoine). Le panneau détaille « dont à consommer / dont vers épargne », et une ligne « = Après budgets » explique enfin pourquoi la colonne sorties peut dépasser les revenus alors que ta balance est positive (mois financé par la tréso déjà en compte).",
      },
      {
        type: "feat",
        text: "Trésorerie — l'écart entre solde réalisé et prévisionnel s'explique : une ligne « ↕ écart » dans le panneau ouvre la passerelle « Du réalisé au prévisionnel » (revenus à recevoir, dépenses à payer, fixes restantes, budgets, épargne — total exact au centime), et une accolade pointillée relie les deux courbes sur le mois actif.",
      },
      {
        type: "feat",
        text: "Trésorerie — zoom par JOUR : depuis le panneau d'un mois, « Détail par jour » ouvre la courbe en escalier jour par jour avec le point bas du mois (anticipation de découvert), les prélèvements datés et la navigation entre mois.",
      },
      {
        type: "improve",
        text: "Les menus déroulants de catégories sont triés automatiquement par fréquence d'utilisation (les plus utilisées en haut), partout dans l'app. Et l'onglet Dépenses run gagne un filtre « — Sans catégorie — ».",
      },
      {
        type: "feat",
        text: "Vie commune — charges fixes partagées : tague une dépense fixe sur ton groupe, définis la répartition, et chaque membre peut l'ajouter à SES dépenses fixes en un clic — une copie liée à SA part, synchronisée en direct (montant, libellé, répartition), verrouillée chez lui. Quand chacun paie sa part, le tricount s'équilibre tout seul.",
      },
      {
        type: "feat",
        text: "Vie commune — répartition PAR DÉFAUT du groupe (ex. 60/40 selon les revenus), réglable dans la roue crantée : toute dépense ou fixe partagée sans répartition explicite l'utilise automatiquement, même rétroactivement. L'onglet est refondu (dépenses visibles directement, barres de répartition sur chaque ligne) et les soldes par membre sont enfin lisibles : « a avancé X · sa part du total : Y » avec un libellé clair (« on te doit » / « doit au groupe »).",
      },
      {
        type: "feat",
        text: "Caisse espèces = une banque à part entière : une fois créée (coche « Reçu en espèces » la première fois), elle apparaît dans la liste des banques (même à 0 €) et se choisit comme n'importe quel compte — un revenu dessus est encaissé direct. Et le sens inverse existe enfin : « Dépôt d'espèces » (caisse → compte bancaire), avec garde-fou si tu déposes plus que le contenu de la caisse.",
      },
      {
        type: "fix",
        text: "Épargne — un budget de versement d'un mois TERMINÉ jamais consommé ne gonfle plus aucune projection : patrimoine, prévision fin d'année du livret, barre annuelle PEE et suivi mensuel déplié sont tous alignés (le budget mort disparaît, comme dans le tableau).",
      },
      {
        type: "fix",
        text: "Revenus — modifier « toute la série » propage désormais aussi le JOUR aux échéances futures (borné à la fin de chaque mois), et le formulaire remplace le switch d'onglets par une simple coche « Appliquer aux occurrences suivantes » près d'Enregistrer.",
      },
      {
        type: "fix",
        text: "Budgets — « Appliquer aux mois suivants » fonctionne aussi sur les séries créées sans lien technique (rapprochement par libellé + catégorie, groupe réparé au passage), en préservant les montants propres à chaque occurrence.",
      },
      {
        type: "improve",
        text: "La catégorie « Dépenses Fixes » a enfin son icône (le marque-page de la barre de navigation), et tu peux la personnaliser dans Réglages comme les autres — le libellé reste protégé.",
      },
      {
        type: "fix",
        text: "Admin — la gestion des accès par module est fiable : les coches sont vérifiées et relues après écriture (erreur affichée si ça n'a pas pris), et les menus de l'utilisateur concerné se mettent à jour tout seuls en moins d'une minute, sans re-connexion.",
      },
      {
        type: "improve",
        text: "Masquer les soldes : le flou des tuiles « Solde global » et « Patrimoine total » est doux et par montant (les libellés restent lisibles), exactement comme dans le tableau — fini le gros rectangle net.",
      },
      {
        type: "feat",
        text: "Nouvelle icône Gestic (la courbe) sur l'app installée et le favicon : le favicon suit le thème de l'app (clair/sombre) en direct, et l'icône du dock/écran d'accueil est lisible dans les deux modes. L'écran de chargement anime désormais la courbe du logo (trait qui se dessine, points qui éclosent, curseur qui grimpe) — fluide, fini le « G » saccadé.",
      },
      {
        type: "feat",
        text: "Gestic Light — vue d'ensemble redessinée : cartes spacieuses, sections dépliées dans un panneau à liseré coloré (on sait toujours ce qu'on regarde), et chaque élément créé (revenu, charge, enveloppe) se CLIQUE pour modifier montant, nom, jour ou banque — appliqué du mois en cours à la fin, les mois passés restent intacts.",
      },
      {
        type: "feat",
        text: "Gestic Light — un avertissement t'invite à créer ton premier compte bancaire si aucun n'existe (tout part de là), et un bouton Réglages en bas de la vue gère le module Groupes d'amis : le toggle fait apparaître/disparaître l'onglet de la navigation instantanément, comme en Pro.",
      },
      {
        type: "feat",
        text: "Gestic Light — l'onglet Prévisionnel monte désormais LE graphique de trésorerie de Budget Pro (courbes réalisé/prévisionnel chaînées, budgets scindés, détail par mois cliquable, zoom par jour) branché sur tes grandes lignes — fini la projection simplifiée qui divergeait.",
      },
      {
        type: "feat",
        text: "Nouveau site public gestic.fr : deux offres claires (Gestic Pro 3,99 €/mois, Gestic Light 2 €/mois, essai gratuit 14 jours) et une section TRANSPARENCE qui montre le vrai décompte de chaque abonnement — frais Stripe expliqués au centime, pourquoi ces tarifs, pas de gratuit financé par tes données. Abonnement en self-service : le paiement active l'accès instantanément, et « Gérer mon abonnement » (facture, carte, résiliation) est dans les Réglages.",
      },
      {
        type: "improve",
        text: "Famille — l'arbre généalogique se réorganise tout seul (auto-layout) : familles recomposées, demi-frères/sœurs, unions multiples… plus aucune superposition, quel que soit le bazar généalogique.",
      },
    ],
  },
  {
    version: "1.5",
    date: "2026-07-03",
    title: "Véhicule, son & confort",
    changes: [
      {
        type: "feat",
        text: "Nouveau module Véhicule : suis chacune de tes voitures — infos générales (immatriculation, série, km à l'achat / actuel, combientième main…), échéances & rappels (assurance, contrôle technique, révision…), historique entretien/réparations et carburant. Accessible depuis le sélecteur de module.",
      },
      {
        type: "feat",
        text: "Véhicule — Motorisation détaillée : une section dédiée avec puissance en ch (DIN) ET en kW (l'un se calcule depuis l'autre), cylindrée en cm³ → litres, nombre de cylindres, soupapes par cylindre, architecture (en ligne / V / boxer… → code L4, V6…), transmission (traction / propulsion / intégrale), boîte de vitesses et huile préconisée.",
      },
      {
        type: "feat",
        text: "Véhicule — Entretien/réparations : choisis une intervention dans une liste de presets (vidange, distribution, freins, pneus avant/arrière…) ou saisis librement, joins les factures (PDF/photo), indique la main (propriétaire) concernée — les entretiens sont regroupés par propriétaire, repliés par défaut. Un entretien avec preset génère automatiquement l'échéance suivante (date approximative).",
      },
      {
        type: "feat",
        text: "Véhicule ↔ Budget pro : à la création d'une voiture, une catégorie budget verrouillée « {Carburant} {Modèle} » (ex. « Diesel Clio ») est créée — tes pleins catégorisés dessus alimentent automatiquement la section carburant (coût, conso). Les échéances futures (avec montant + banque) créent des budgets prévisionnels au bon mois/année dans Budget pro, et tes entretiens peuvent créer la dépense liée (bidirectionnelle).",
      },
      {
        type: "feat",
        text: "Véhicule — Carburant & conso : renseigne le litrage sur chaque dépense carburant (badge « À faire » tant que c'est vide) et le kilométrage de temps en temps → conso moyenne (L/100 km) et coût au 100 km calculés. Une roue crantée règle la périodicité d'un rappel de relevé kilométrique (notification).",
      },
      {
        type: "feat",
        text: "Véhicule — Pneumatiques & roues : une section dédiée pour les dimensions des pneus (avant et arrière séparément s'ils diffèrent, ex. 205/55 R16), la saison (été / hiver / 4 saisons), les jantes (alliage ou tôle + diamètre en pouces) et les pressions recommandées AV/AR (en bar).",
      },
      {
        type: "feat",
        text: "Musique — Matos : chaque équipement gère désormais ses ENTRÉES en plus de ses sorties — une console a des canaux d'entrée, des enceintes ont une entrée, etc. (avant : sorties uniquement).",
      },
      {
        type: "feat",
        text: "Musique — Patch « système de diffusion » : si tu gères toi-même la diffusion, active ce mode et ajoute ta console + tes enceintes. Tu routes alors chaque sortie d'instrument vers une entrée de console, en passant par un intermédiaire (DI active/passive, pedalboard, préampli, ampli repiqué…), avec l'alimentation 48V signalée (suggérée automatiquement pour les condensateurs / DI actives) et les effets/inserts. L'export PDF du patch est enrichi (48V, intermédiaire, effets, routage) pour l'ingé son.",
      },
      {
        type: "feat",
        text: "Musique — Setlists : bouton « Dupliquer » pour cloner une setlist et tous ses morceaux. Affectation du matos à un groupe (avec la config utilisée et le membre assigné), et events enrichis (setlist liée + champs par type : concert / répétition / enregistrement).",
      },
      {
        type: "feat",
        text: "Rando — Matos : icônes par catégorie (cliquables, tu choisis l'emoji dans une grille), nouvel onglet « Mon catalogue » qui regroupe tout ton matériel (inventaire global), et colonne « référence » produit.",
      },
      {
        type: "feat",
        text: "Admin — Accès fin par module : pour chaque utilisateur, donne ou retire l'accès module par module en un clic (Budget, Musique, Rando, Voiture…), en plus du plan « Gestic Pro » qui débloque tous les modules.",
      },
      {
        type: "improve",
        text: "Navigation mobile repensée : une pastille flottante en bas à gauche (avec la page courante) + la cloche de notifications. Au tap, une feuille glisse du bas avec le changement de module et les onglets en grille (grandes zones de tap) — fini la barre du bas serrée à scroll.",
      },
      {
        type: "improve",
        text: "La cloche de notifications est désormais visible dans TOUS les modules (avant : seulement dans Budget).",
      },
      {
        type: "improve",
        text: "Dashboard — tuile « Patrimoine total » : les lignes du patrimoine financier (placements) et corporel sont cliquables et renvoient directement vers l'onglet Possessions.",
      },
      {
        type: "improve",
        text: "Gestic Light : le récap « Mon mois » est plus aéré (récurrents / charges / enveloppes en menus dépliants, sans doublon en bas de page), et la liste « Mes comptes » passe sur deux lignes (libellé non tronqué, puis MàJ / solde / recaler).",
      },
    ],
  },
  {
    version: "1.4",
    date: "2026-06-16",
    title: "Imports, accès & confort",
    changes: [
      {
        type: "fix",
        text: "Mobile (PWA iOS) : la passkey ne devrait plus se redemander en boucle. Après vérification, l'app mémorise l'appareil via une navigation fiable (le mécanisme précédent ne persistait pas toujours sur iOS en app installée).",
      },
      {
        type: "feat",
        text: "Réglages → Comptes bancaires : tu peux réordonner tes comptes (flèches ↑/↓). L'ordre s'applique partout — capsule des soldes, dashboard, et le compte proposé par défaut à la saisie d'une dépense/revenu/budget (= le 1er de ta liste).",
      },
      {
        type: "improve",
        text: "Mobile — Dépenses run : les boutons « Import CSV » et « Règles auto » n'affichent plus que leur icône (le texte mangeait la place et faisait passer « + Dépense » à la ligne).",
      },
      {
        type: "improve",
        text: "Projets communs — droits resserrés : gérer les membres d'un projet est désormais réservé à l'admin du groupe ou au créateur du projet, et exclure un membre d'une dépense est réservé à l'admin ou au créateur de la dépense. Les autres membres voient ces écrans en lecture seule.",
      },
      {
        type: "feat",
        text: "Projets communs : l'admin du groupe (ou le créateur du projet) peut « Archiver » un projet. Il sort de la liste active et sa catégorie est masquée des sélecteurs chez tous les membres — mais les dépenses passées sont conservées. Un bouton « Projets archivés » en bas de la liste permet de les rouvrir pour consulter les données, et de les réactiver.",
      },
      {
        type: "improve",
        text: "Nouvelle catégorie : la couleur est tirée au hasard dans la palette par défaut (au lieu d'un gris systématique) — y compris quand on crée la catégorie à la volée depuis le menu déroulant d'une dépense/revenu/budget.",
      },
      {
        type: "improve",
        text: "Possessions : les sous-catégories (groupes) du patrimoine corporel sont repliées par défaut. Si tu en déplies une, ça reste déplié 24 h puis ça revient à l'état replié.",
      },
      {
        type: "improve",
        text: "Réglages : la section « Catégories » (longue) est maintenant pliable et repliée par défaut — clique son titre pour la déplier.",
      },
      {
        type: "improve",
        text: "Écrans vides (Budgets, Revenus, Dépenses) : la grosse icône « + » est désormais cliquable pour ouvrir directement le formulaire d'ajout — comme sur l'écran « créer un compte ».",
      },
      {
        type: "feat",
        text: "Dépense → patrimoine : en ajoutant une dépense, « plus d'options » propose « Ajouter au patrimoine corporel ». Coche-la (ex. l'achat d'un écran) et une ligne est créée dans Possessions à la valeur de la dépense — avec le libellé de la dépense pré-rempli (modifiable) et le choix de la catégorie patrimoine.",
      },
      {
        type: "improve",
        text: "Vie commune — onglets réordonnés : « Vie commune » en premier, puis « Projets communs ». Le « Classement patrimoine » est caché par défaut (peu pertinent pour une coloc/un couple) ; tu peux le ré-afficher quand tu veux depuis les réglages du groupe (il revient alors en 3ᵉ onglet).",
      },
      {
        type: "feat",
        text: "Vie commune — bouton « Rembourser » : dans la liste « qui doit combien à qui », tu rembourses un membre en un clic. Ça crée la dépense chez toi (depuis la banque choisie) et règle le solde commun immédiatement ; le destinataire reçoit une demande (notification + bandeau dans Revenus + onglet Vie commune) et choisit lui-même sur quel compte l'encaisser (perso ou compte commun) — pas de compte deviné à sa place. Le libellé et le montant restent liés entre les deux (le pointage, lui, reste propre à chacun).",
      },
      {
        type: "feat",
        text: "Vie commune — charges fixes : une dépense fixe peut « concerner la vie commune » (case à la création comme à la modification). L'impact n'a lieu qu'au moment où tu coches l'échéance comme « payée » : la dépense générée alimente alors le suivi commun.",
      },
      {
        type: "improve",
        text: "Barre latérale : les groupes (amis / vie commune) sont listés directement sous un en-tête « Groupes » — on clique sur le groupe voulu, plus de ligne de module intermédiaire. Un raccourci « Créer un groupe » est ajouté en bas de la liste.",
      },
      {
        type: "improve",
        text: "Notification de demande d'adhésion : cliquer dessus ouvre directement le groupe concerné sur l'onglet « Demandes » (validation en un coup d'œil), au lieu d'un atterrissage générique.",
      },
      {
        type: "feat",
        text: "Vie commune : on peut marquer (ou retirer) « Concerne la vie commune » sur une dépense déjà créée, en cliquant dessus pour la modifier — plus besoin de le décider à la saisie.",
      },
      {
        type: "feat",
        text: "Vie commune : répartition personnalisée. Par défaut une dépense commune est partagée à parts égales, mais un bouton « camembert » sur la ligne permet de pondérer la part de chacun en % (les autres se recalculent tout seuls pour faire 100 %), avec un raccourci « 100 % » pour dire « c'est untel qui rembourse tout ».",
      },
      {
        type: "feat",
        text: "Vie commune (coloc / couple, sans limite de membres) : nouveau type de groupe avec son onglet dédié — le compte courant et le solde de chacun, et qui doit combien à qui (style tricount). Coche « Concerne la vie commune » dans « plus d'options » d'une dépense pour l'y intégrer ; la liste des dépenses est masquée par défaut (un clic l'affiche, avec filtre par membre). Tu peux créer plusieurs groupes (amis ou vie commune) et passer de l'un à l'autre depuis la barre latérale (section « Groupes ») ou le sélecteur en haut du module.",
      },
      {
        type: "feat",
        text: "Compte commun : partage un compte bancaire avec un autre utilisateur Gestic (coche « Compte commun » à la création → invitation → acceptation). Tout devient commun et en direct : opérations (dépenses/revenus), solde, catégories propres au compte, charges fixes et budgets. Le solde affiché reste entier ; dans le patrimoine et le classement, chacun ne compte que SA part — calculée automatiquement d'après qui a alimenté le compte, et réglable à la main par le créateur.",
      },
      {
        type: "fix",
        text: "Suppression d'un compte bancaire : un bouton 🗑️ (avec confirmation « attention, irréversible ») dans la fiche du compte permet enfin de le supprimer — avec toutes ses opérations, charges fixes et budgets en cascade. Avant, c'était impossible.",
      },
      {
        type: "feat",
        text: "Patrimoine corporel : propriété partagée. Sur chaque bien, une icône de partage permet d'indiquer ta part de détention (déf. 50 %) — seule ta part compte dans ton patrimoine et le classement, la valeur totale restant affichée en petit. Tu peux aussi partager le bien avec un autre utilisateur Gestic (par email) : il reçoit une notification et une demande dans ses Possessions, et à l'acceptation le bien apparaît chez lui (lecture seule, à sa part), avec sa catégorie créée au besoin, et reste synchronisé en temps réel — tu restes maître de la valeur.",
      },
      {
        type: "improve",
        text: "Patrimoine partagé : un bien immobilier partagé affiche aussi ses prêts côté co-détenteur (flèche jaune, en lecture seule) et permet de choisir l'affichage net/brut — sans aucun impact sur sa trésorerie (les prêts restent gérés par le propriétaire).",
      },
      {
        type: "feat",
        text: "Patrimoine partagé : tu peux indiquer une part de DETTE différente de ta part du bien (ex. 50 % du bien mais 60 % du prêt). Ça n'ajuste que le patrimoine net (jamais les échéances) ; le co-détenteur prend automatiquement le complément.",
      },
      {
        type: "fix",
        text: "Sécurité : un compte non autorisé (email non dans la liste, ou accès retiré) ne peut plus entrer dans l'app — il est renvoyé sur la page « accès non autorisé », sans aucune navigation ni module visible.",
      },
      {
        type: "fix",
        text: "Réglages : les catégories ne « sautent » plus toutes les 2 secondes (boucle de synchronisation temps réel corrigée + ordre d'affichage stabilisé).",
      },
      {
        type: "fix",
        text: "Patrimoine corporel : la coche « montant net » d'un bien immobilier se met à jour correctement, et le camembert n'affiche plus de pourcentages aberrants (>100 %) quand un bien est en net.",
      },
      {
        type: "improve",
        text: "Possessions : patrimoine financier et corporel sont désormais deux cartes neutres cohérentes avec le reste de l'app, différenciées par un simple accent d'icône (vert / ambre) — fini le fond vert qui faisait « bilan/synthèse ».",
      },
      {
        type: "feat",
        text: "Patrimoine corporel : on peut joindre PLUSIEURS documents (PDF/images) sur une même ligne.",
      },
      {
        type: "improve",
        text: "Patrimoine corporel : la catégorie « Immobilier » est verrouillée (non supprimable ni renommable, elle reste unique), et deux catégories ne peuvent plus porter le même nom. Une ligne ajoutée à la main dans Immobilier est un vrai bien (liable à un emprunt, le montant saisi = ton apport).",
      },
      {
        type: "improve",
        text: "Démarrage instantané : au lancement (même après une mise en veille iOS), l'app affiche tout de suite ta dernière vue puis se met à jour en arrière-plan — fini l'écran de chargement « G » à froid.",
      },
      {
        type: "feat",
        text: "Temps réel : tes données se mettent à jour toutes seules, sans rechargement — si tu modifies sur un autre appareil, l'app suit ; et le classement patrimoine du groupe d'amis se rafraîchit en direct quand un membre bouge.",
      },
      {
        type: "feat",
        text: "Dépenses partagées (tricount) en direct : quand un ami ajoute, modifie ou supprime une dépense partagée, ça apparaît chez tout le monde immédiatement, sans bouton ni rechargement.",
      },
      {
        type: "feat",
        text: "Dépenses run : sur les lignes de dépenses partagées (catégorie de groupe d'amis), un bouton 👥 permet d'exclure directement les membres non concernés — sans passer par le module.",
      },
      {
        type: "feat",
        text: "Tricount : clique sur « x membres » dans un projet pour gérer qui en fait partie — exclure complètement quelqu'un (ex. absent des vacances) ou le réintégrer.",
      },
      {
        type: "feat",
        text: "Groupe d'amis : l'onglet « Dépenses partagées » devient « Projets communs ». Dans un projet, deux sous-onglets : les dépenses partagées, et une nouvelle Liste de tâches (todo partagée, en temps réel) avec affectation des tâches aux membres.",
      },
      {
        type: "feat",
        text: "Groupe d'amis : un chat de groupe (icône 💬 à droite des onglets) — messages en temps réel, style moderne, avec auteur et horodatage.",
      },
      {
        type: "feat",
        text: "Classement patrimoine : deux vues Net / Brut. Tout le monde est comparé sur la même base, donc plus de classement faussé si quelqu'un met son immobilier en brut et un autre en net.",
      },
      {
        type: "improve",
        text: "Liste de tâches : deux vues (Liste / Par membre, avec sections combinées « Quentin + Julien »), un filtre multi-membres (toi toujours en premier) pour ne voir que les tâches de qui tu veux, et un clic sur une tâche pour éditer son libellé et ses membres concernés.",
      },
      {
        type: "fix",
        text: "Groupe d'amis : le bouton « Rafraîchir » du classement met enfin à jour les chiffres (avant il fallait recharger la page).",
      },
      {
        type: "fix",
        text: "Sur mobile, l'app ne redemande plus la passkey en permanence : la vérification est mémorisée 6 mois et se prolonge automatiquement à chaque utilisation.",
      },
      {
        type: "fix",
        text: "Accès aux modules cloisonné par offre : un compte « Budget » n'a plus accès à Musique, Rando ou Famille (réservés à l'offre complète).",
      },
      {
        type: "improve",
        text: "Mise à jour rapide du solde d'un PEA / placement depuis Possessions : tu saisis le nouveau solde total, la plus ou moins-value est calculée automatiquement.",
      },
      {
        type: "fix",
        text: "Ajout d'une dépense depuis un budget : les budgets déjà entièrement consommés ne sont plus proposés.",
      },
      {
        type: "fix",
        text: "Menus déroulants désormais lisibles en mode sombre (fini le texte blanc sur fond blanc).",
      },
      {
        type: "improve",
        text: "Le curseur prend la forme d'une main au survol de tous les éléments cliquables.",
      },
      {
        type: "feat",
        text: "Notes de version (ce changelog) accessibles depuis la sidebar sur ordi et le menu en haut à gauche sur mobile.",
      },
      {
        type: "feat",
        text: "Import des relevés Excel (.xlsx) en plus du CSV — le fichier est lu directement dans le navigateur, rien n'est envoyé sur un serveur.",
      },
      {
        type: "feat",
        text: "Historique des imports bancaires : on retrouve chaque import passé et on peut le supprimer. La suppression retire d'un coup TOUTES les transactions créées par cet import — garde-fou pour revenir en arrière sans crise.",
      },
      {
        type: "feat",
        text: "Import bancaire : enregistre des modèles de mapping de colonnes et réutilise-les en un clic — fini de refaire l'association des colonnes à la main quand l'auto-détection se trompe. Un fichier au même format réapplique le bon modèle tout seul.",
      },
      {
        type: "fix",
        text: "Ajout d'une dépense / d'un revenu : un montant saisi avec un signe ou un espace devant (« -50 », « − 50 ») n'échoue plus — le signe est nettoyé automatiquement.",
      },
      {
        type: "fix",
        text: "Import CSV : les débits exportés avec un « - » en préfixe sont désormais correctement importés.",
      },
      {
        type: "feat",
        text: "Groupe d'amis : rejoindre par code crée désormais une demande d'adhésion que l'organisateur valide dans un nouvel onglet « Demandes ». Le code de partage change automatiquement tous les 7 jours (les anciens codes expirent).",
      },
      {
        type: "improve",
        text: "Réglages → Groupe d'amis : tu vois tes groupes en cours et les invitations reçues directement ici (même si le module est désactivé), et l'organisateur peut renommer chaque membre pour l'affichage.",
      },
      {
        type: "feat",
        text: "Notifications groupe d'amis : tu reçois une notif quand on t'invite, et l'organisateur est prévenu quand quelqu'un demande à rejoindre par code. Les notifications sont cliquables (elles ouvrent directement la bonne page).",
      },
      {
        type: "fix",
        text: "Groupe d'amis : « Rejoindre par code » fonctionne enfin (l'ancienne version échouait toujours), avec un message d'erreur clair en cas de souci.",
      },
      {
        type: "fix",
        text: "Dépenses partagées (groupe d'amis / tricount) : les dépenses taguées remontent enfin dans le partage.",
      },
      {
        type: "improve",
        text: "Écrans d'erreur aux couleurs de l'app : un plantage ne fait plus basculer l'interface en thème clair.",
      },
    ],
  },
  {
    version: "1.3",
    title: "Patrimoine, emprunts & trésorerie",
    changes: [
      {
        type: "feat",
        text: "Emprunts immobiliers reliés à un bien : valeur brute / nette, plusieurs prêts sur un même bien, et ajout d'un apport manuel hors emprunts bancaires.",
      },
      {
        type: "improve",
        text: "Graphique de trésorerie prévisionnel aligné sur le tableau principal : courbes d'épargne sélectionnables et mémorisées, fusion prévi + épargne, vue agrandie.",
      },
      {
        type: "improve",
        text: "Versements épargne : les budgets déjà épuisés sont masqués, et un bouton « réduire le versement » apparaît quand c'est plus pertinent que de renflouer.",
      },
      {
        type: "fix",
        text: "Onglet Clients : les ventes prévues et réalisées remontent dans le tableau de bord.",
      },
    ],
  },
  {
    version: "1.2",
    title: "Collaboratif & nouveaux modules",
    changes: [
      {
        type: "feat",
        text: "Groupe d'amis : rejoindre par code, invitations par email, et dépenses partagées (catégories verrouillées synchronisées entre membres).",
      },
      {
        type: "feat",
        text: "Module Musique : groupes, répertoire, patches, matos et calendrier.",
      },
      {
        type: "feat",
        text: "Module Rando : groupes, itinéraires, matériel et partage en lecture seule.",
      },
      {
        type: "feat",
        text: "Module Famille : arbre généalogique.",
      },
    ],
  },
  {
    version: "1.1",
    title: "Budget avancé",
    changes: [
      {
        type: "feat",
        text: "Multi-banques, charges fixes, et import CSV des relevés bancaires avec déduplication automatique.",
      },
      {
        type: "feat",
        text: "Intermittence du spectacle et portefeuille pro (PEE / abondement).",
      },
      {
        type: "feat",
        text: "Règles de catégorisation automatique des dépenses importées.",
      },
    ],
  },
  {
    version: "1.0",
    title: "Lancement de Gestic",
    changes: [
      {
        type: "feat",
        text: "Budget personnel : dépenses, revenus, catégories, comptes et banques.",
      },
      {
        type: "feat",
        text: "Épargne, tableau de bord et prévisions de solde.",
      },
      {
        type: "feat",
        text: "Application installable (PWA) avec sauvegarde et restauration des données.",
      },
    ],
  },
];
